import SHDModal from '@ui/Modal/SHDModal';
import SHDThemeOverride from '@ui/ThemeOverride/SHDThemeOverride';
import React, { createContext, useContext, useState, ReactNode } from 'react';

interface ModalContextProps {
  showModal: (content: ReactNode, options?: ShowModalOptions) => void;
  hideModal: () => void;
}

interface ShowModalOptions {
  className?: string;
  isFixedHeight?: boolean;
  isRelativeHeight?: boolean;
  isConfirmation?: boolean;
  isFixedWidth?: boolean;
  onClose?: () => void;
  onGoBack?: () => void;
}

const ModalContext = createContext<ModalContextProps | undefined>(undefined);

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};

export const ModalProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalClassName, setModalClassName] = useState<string[]>([]);
  const [modalContent, setModalContent] = useState<ReactNode | null>(null);
  const [modalOnClose, setModalOnClose] = useState<(() => void) | undefined>();
  const [modalOnGoBack, setModalOnGoBack] = useState<
    (() => void) | undefined
  >();
  const [modalIsFixedHeight, setModalIsFixedHeight] = useState<
    boolean | undefined
  >(false);

  const [modalIsRelativeHeight, setModalIsRelativeHeight] = useState<
    boolean | undefined
  >(false);

  const [modalIsConfirmation, setModalIsConfirmation] = useState<
    boolean | undefined
  >(false);

  const [modalIsFixedWidth, setModalIsFixedWidth] = useState<
    boolean | undefined
  >(false);

  const showModal = (content: ReactNode, options?: ShowModalOptions) => {
    const {
      onGoBack,
      isFixedHeight,
      onClose,
      isRelativeHeight,
      className,
      isConfirmation,
      isFixedWidth,
    } = options || {};

    setModalContent(content);
    setModalOnGoBack(() => onGoBack);
    setIsModalOpen(true);
    setModalIsFixedHeight(isFixedHeight);
    setModalOnClose(() => onClose);
    setModalIsRelativeHeight(isRelativeHeight);
    setModalIsConfirmation(isConfirmation);
    setModalIsFixedWidth(isFixedWidth);
    setModalClassName((prev) => {
      let additionalClassName: string[] = [];
      if (className) {
        additionalClassName = className.split(' ');
      }
      return [...prev, ...additionalClassName];
    });
  };

  const hideModal = () => {
    setIsModalOpen(false);
    setModalContent(null);
    setModalOnGoBack(undefined);
    const closeHandler = modalOnClose;
    setModalOnClose(undefined);
    if (closeHandler) {
      closeHandler();
    }
  };

  return (
    <ModalContext.Provider value={{ showModal, hideModal }}>
      {children}

      {isModalOpen && (
        <SHDThemeOverride>
          <SHDModal
            className={modalClassName.join(' ')}
            isFixedHeight={modalIsFixedHeight}
            isRelativeHeight={modalIsRelativeHeight}
            isConfirmation={modalIsConfirmation}
            isFixedWidth={modalIsFixedWidth}
            onClose={() => {
              hideModal();
            }}
            onGoBack={modalOnGoBack}
          >
            {modalContent}
          </SHDModal>
        </SHDThemeOverride>
      )}
    </ModalContext.Provider>
  );
};
